// src/components/Admin/AdminLeaveRequests.jsx - Versione completa con funzionalità eliminazione
import React, { useState, useEffect } from 'react';
import { 
  getAllLeaveRequests, 
  updateLeaveRequestStatusWithSync, 
  deleteApprovedRequestWithDesync 
} from '../../firebase';
import Notification from '../Notification';

const AdminLeaveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  
  // Filtri con ricerca utente
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateFrom: '',
    dateTo: '',
    user: ''
  });
  
  // Stato per ricerca utente
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  
  // Stato per gestione note/azioni
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(false);
  
  // Stato per gestione eliminazione richieste approvate
  const [isDeleting, setIsDeleting] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Carica tutte le richieste
  useEffect(() => {
    const fetchRequests = async () => {
      setIsLoading(true);
      try {
        const allRequests = await getAllLeaveRequests();
        setRequests(allRequests);
        setFilteredRequests(allRequests);
        
        // Estrai utenti unici
        const usersMap = new Map();
        allRequests.forEach(request => {
          if (request.userEmail) {
            const userData = {
              email: request.userEmail,
              nome: request.userNome || '',
              cognome: request.userCognome || '',
              displayName: ''
            };
            
            if (userData.nome && userData.cognome) {
              userData.displayName = `${userData.nome} ${userData.cognome}`;
            } else if (request.userName) {
              userData.displayName = request.userName;
            } else {
              userData.displayName = request.userEmail;
            }
            
            usersMap.set(request.userEmail, userData);
          }
        });
        
        const usersArray = Array.from(usersMap.values()).sort((a, b) => 
          a.displayName.localeCompare(b.displayName)
        );
        setAvailableUsers(usersArray);
        setError(null);
      } catch (err) {
        console.error("Errore caricamento richieste:", err);
        setError("Impossibile caricare le richieste. Riprova più tardi.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, []);

  // Filtra utenti per ricerca
  useEffect(() => {
    if (userSearchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowUserSuggestions(false);
      return;
    }

    const searchTerm = userSearchTerm.toLowerCase();
    const filtered = availableUsers.filter(user => {
      return (
        user.displayName.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        (user.nome && user.nome.toLowerCase().includes(searchTerm)) ||
        (user.cognome && user.cognome.toLowerCase().includes(searchTerm))
      );
    });

    setFilteredUsers(filtered);
    setShowUserSuggestions(true);
  }, [userSearchTerm, availableUsers]);
  
  // Applica filtri
  useEffect(() => {
    let result = [...requests];
    
    if (filters.status) {
      result = result.filter(request => request.status === filters.status);
    }
    
    if (filters.type) {
      result = result.filter(request => request.type === filters.type);
    }
    
    if (filters.user) {
      result = result.filter(request => 
        request.userEmail && request.userEmail.toLowerCase().includes(filters.user.toLowerCase())
      );
    }
    
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      result = result.filter(request => {
        const requestDate = new Date(request.dateFrom);
        return requestDate >= dateFrom;
      });
    }
    
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      result = result.filter(request => {
        const requestDate = new Date(request.dateFrom);
        return requestDate <= dateTo;
      });
    }
    
    setFilteredRequests(result);
  }, [filters, requests]);

  // Sincronizza campo ricerca con filtro utente
  useEffect(() => {
    if (filters.user && availableUsers.length > 0) {
      const selectedUser = availableUsers.find(user => user.email === filters.user);
      if (selectedUser && userSearchTerm !== selectedUser.displayName) {
        setUserSearchTerm(selectedUser.displayName);
      }
    } else if (!filters.user && userSearchTerm) {
      setUserSearchTerm('');
    }
  }, [filters.user, availableUsers]);
  
  // Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchTerm(value);
    if (value.trim() === '') {
      setFilters(prev => ({ ...prev, user: '' }));
    }
  };

  const handleUserSelect = (user) => {
    setUserSearchTerm(user.displayName);
    setShowUserSuggestions(false);
    setFilters(prev => ({ ...prev, user: user.email }));
  };

  const handleUserSearchBlur = () => {
    setTimeout(() => setShowUserSuggestions(false), 200);
  };

  const handleUserSearchFocus = () => {
    if (userSearchTerm.trim() !== '' && filteredUsers.length > 0) {
      setShowUserSuggestions(true);
    }
  };
  
  const resetFilters = () => {
    setFilters({ status: '', type: '', dateFrom: '', dateTo: '', user: '' });
    setUserSearchTerm('');
    setShowUserSuggestions(false);
  };
  
  const showNotes = (request, defaultStatus) => {
    setSelectedRequest({ ...request, defaultStatus });
    setAdminNotes(request.adminNotes || '');
    setShowNotesForm(true);
  };
  
  const handleRequestAction = async (approve = true) => {
    if (!selectedRequest) return;
    
    setProcessingRequest(true);
    try {
      const newStatus = approve ? 'approved' : 'rejected';
      
      if (approve) {
        setNotification({
          show: true,
          message: 'Approvazione in corso...',
          type: 'info'
        });
      }
      
      const updatedRequest = await updateLeaveRequestStatusWithSync(
        selectedRequest.id, 
        newStatus, 
        adminNotes
      );
      
      setRequests(prev => 
        prev.map(req => req.id === updatedRequest.id ? updatedRequest : req)
      );
      
      setShowNotesForm(false);
      setSelectedRequest(null);
      setAdminNotes('');
      
      setNotification({
        show: true,
        message: `Richiesta ${approve ? 'approvata' : 'rifiutata'} con successo!`,
        type: 'success'
      });
      
    } catch (err) {
      console.error('Errore gestione richiesta:', err);
      setNotification({
        show: true,
        message: `Errore: ${err.message}`,
        type: 'error'
      });
    } finally {
      setProcessingRequest(false);
    }
  };

  // Mostra la conferma di eliminazione
  const showDeleteConfirmation = (request) => {
    setRequestToDelete(request);
    setShowDeleteConfirm(true);
  };

  // Gestisce l'eliminazione di una richiesta approvata
  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    
    setIsDeleting(true);
    
    try {
      console.log('Eliminazione richiesta approvata:', requestToDelete);
      
      // Mostra notifica di elaborazione
      setNotification({
        show: true,
        message: 'Eliminazione in corso... desincronizzazione dal calendario ore',
        type: 'info'
      });
      
      // Elimina la richiesta con desincronizzazione automatica
      const deleteResult = await deleteApprovedRequestWithDesync(requestToDelete.id);
      
      console.log('Richiesta eliminata:', deleteResult);
      
      // Rimuovi la richiesta dalla lista
      setRequests(prevRequests => 
        prevRequests.filter(req => req.id !== requestToDelete.id)
      );
      
      // Chiudi il dialog di conferma
      setShowDeleteConfirm(false);
      setRequestToDelete(null);
      
      // Prepara il messaggio di successo
      let successMessage = 'Richiesta eliminata con successo';
      
      if (deleteResult.desyncResult) {
        if (deleteResult.desyncResult.success) {
          successMessage += `\n✅ Calendario ore ripristinato automaticamente (${deleteResult.desyncResult.datesDesynchronized} date ripristinate)`;
        } else {
          successMessage += '\n⚠️ Attenzione: errore nella desincronizzazione automatica del calendario ore. Verifica manualmente.';
        }
      }
      
      // Mostra notifica di successo
      setNotification({
        show: true,
        message: successMessage,
        type: 'success'
      });
      
      // Nascondi la notifica dopo 6 secondi
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 6000);
      
    } catch (err) {
      console.error('Errore nell\'eliminazione della richiesta:', err);
      
      setNotification({
        show: true,
        message: `Errore nell'eliminazione della richiesta: ${err.message}`,
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime24h = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
  };
  
  const getRequestTypeName = (type) => {
    switch (type) {
      case 'permission': return 'Permesso';
      case 'vacation': return 'Ferie';
      case 'sickness': return 'Malattia';
      default: return type;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'In attesa';
      case 'approved': return 'Approvata';
      case 'rejected': return 'Rifiutata';
      default: return status;
    }
  };

  return (
    <div style={{ padding: '20px', background: '#f8f9fa', minHeight: '100vh' }}>
      <h2 style={{ marginBottom: '25px', color: '#2c3e50' }}>
        Gestione Richieste Permessi/Ferie/Malattia
      </h2>
      
      {/* Notifiche */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        />
      )}

      {/* Info box sull'eliminazione e desincronizzazione */}
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '20px', marginRight: '10px' }}>🗑️</span>
          <strong>Eliminazione Richieste Approvate</strong>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
          Puoi eliminare richieste già approvate. Il sistema ripristinerà automaticamente il calendario ore 
          rimuovendo le lettere speciali (F, P, M) dalle date interessate e riportandole allo stato vuoto.
        </p>
      </div>
      
      {/* Sezione Filtri */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginBottom: '15px', color: '#495057' }}>Filtri Richieste</h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          alignItems: 'end',
          marginBottom: '15px'
        }}>
          {/* Filtro Stato */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontWeight: '600', marginBottom: '5px', color: '#333' }}>
              Stato:
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">Tutti gli stati</option>
              <option value="pending">In attesa</option>
              <option value="approved">Approvata</option>
              <option value="rejected">Rifiutata</option>
            </select>
          </div>

          {/* Filtro Tipo */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontWeight: '600', marginBottom: '5px', color: '#333' }}>
              Tipo:
            </label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">Tutti i tipi</option>
              <option value="permission">Permesso</option>
              <option value="vacation">Ferie</option>
              <option value="sickness">Malattia</option>
            </select>
          </div>

          {/* Ricerca Utente */}
          <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <label style={{ fontWeight: '600', marginBottom: '5px', color: '#333' }}>
              Cerca Utente:
            </label>
            <input
              type="text"
              value={userSearchTerm}
              onChange={handleUserSearchChange}
              onFocus={handleUserSearchFocus}
              onBlur={handleUserSearchBlur}
              placeholder="Digita nome, cognome o email..."
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            
            {/* Suggerimenti utenti */}
            {showUserSuggestions && filteredUsers.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '2px solid #007bff',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {filteredUsers.map((user, index) => (
                  <div
                    key={index}
                    onClick={() => handleUserSelect(user)}
                    style={{
                      padding: '10px 15px',
                      cursor: 'pointer',
                      borderBottom: index < filteredUsers.length - 1 ? '1px solid #f0f0f0' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: 'bold', color: '#333' }}>
                      {user.displayName}
                    </div>
                    <small style={{ color: '#666' }}>
                      {user.email}
                    </small>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Data Da */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontWeight: '600', marginBottom: '5px', color: '#333' }}>
              Data Da:
            </label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Data A */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontWeight: '600', marginBottom: '5px', color: '#333' }}>
              Data A:
            </label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Reset Button */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 16px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Reset Filtri
            </button>
          </div>
        </div>

        {/* Filtri Attivi */}
        {(filters.status || filters.type || filters.user || filters.dateFrom || filters.dateTo) && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <strong>Filtri attivi:</strong>
            {filters.status && (
              <span style={{
                background: '#e9ecef',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#495057'
              }}>
                Stato: {getStatusText(filters.status)}
              </span>
            )}
            {filters.type && (
              <span style={{
                background: '#e9ecef',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#495057'
              }}>
                Tipo: {getRequestTypeName(filters.type)}
              </span>
            )}
            {filters.user && (
              <span style={{
                background: '#e9ecef',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#495057'
              }}>
                Utente: {availableUsers.find(u => u.email === filters.user)?.displayName || filters.user}
              </span>
            )}
            {filters.dateFrom && (
              <span style={{
                background: '#e9ecef',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#495057'
              }}>
                Da: {formatDate(filters.dateFrom)}
              </span>
            )}
            {filters.dateTo && (
              <span style={{
                background: '#e9ecef',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#495057'
              }}>
                A: {formatDate(filters.dateTo)}
              </span>
            )}
          </div>
        )}

        {/* Statistiche */}
        <div style={{ color: '#666', fontSize: '14px' }}>
          Visualizzate <strong>{filteredRequests.length}</strong> di <strong>{requests.length}</strong> richieste totali
        </div>
      </div>

      {/* Contenuto Principale */}
      {error ? (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#dc3545',
          border: '1px solid #dc3545'
        }}>
          {error}
        </div>
      ) : isLoading ? (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#666'
        }}>
          Caricamento richieste...
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Utente
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Tipo
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Periodo
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Stato
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Data Richiesta
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      {requests.length === 0 ? 'Nessuna richiesta trovata' : 'Nessuna richiesta corrisponde ai filtri selezionati'}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map(request => (
                    <tr key={request.id} style={{
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#333' }}>
                            {request.userNome && request.userCognome 
                              ? `${request.userNome} ${request.userCognome}`
                              : request.userName || 'Nome non disponibile'}
                          </div>
                          <small style={{ color: '#666', fontSize: '12px' }}>
                            {request.userEmail}
                          </small>
                        </div>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: request.type === 'permission' ? '#e3f2fd' : 
                                     request.type === 'vacation' ? '#e8f5e8' : '#ffebee',
                          color: request.type === 'permission' ? '#1976d2' : 
                                 request.type === 'vacation' ? '#388e3c' : '#d32f2f'
                        }}>
                          {getRequestTypeName(request.type)}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        <div>
                          <div>{formatDate(request.dateFrom)}</div>
                          {request.dateTo && request.dateTo !== request.dateFrom && (
                            <div style={{ color: '#666' }}>→ {formatDate(request.dateTo)}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: request.status === 'pending' ? '#fff3cd' : 
                                     request.status === 'approved' ? '#d4edda' : '#f8d7da',
                          color: request.status === 'pending' ? '#856404' : 
                                 request.status === 'approved' ? '#155724' : '#721c24'
                        }}>
                          {getStatusText(request.status)}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        {request.createdAt ? 
                          formatDate(request.createdAt.toDate ? request.createdAt.toDate() : request.createdAt) 
                          : '-'}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => showNotes(request, 'approved')}
                                disabled={processingRequest || isDeleting}
                                style={{
                                  padding: '4px 8px',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}
                              >
                                Approva
                              </button>
                              <button
                                onClick={() => showNotes(request, 'rejected')}
                                disabled={processingRequest || isDeleting}
                                style={{
                                  padding: '4px 8px',
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}
                              >
                                Rifiuta
                              </button>
                            </>
                          )}
                          
                          {/* Pulsante Elimina per richieste approvate */}
                          {request.status === 'approved' && (
                            <button
                              onClick={() => showDeleteConfirmation(request)}
                              disabled={processingRequest || isDeleting}
                              style={{
                                padding: '4px 8px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: '500',
                                marginBottom: '3px'
                              }}
                              title="Elimina richiesta e desincronizza dal calendario ore"
                            >
                              {isDeleting && requestToDelete?.id === request.id ? 'Eliminazione...' : 'Elimina'}
                            </button>
                          )}
                          
                          <button
                            onClick={() => showNotes(request, request.status)}
                            style={{
                              padding: '4px 8px',
                              background: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            Dettagli
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal per Note */}
      {showNotesForm && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            {/* Header Modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #dee2e6'
            }}>
              <h4 style={{ margin: 0, color: '#495057' }}>
                {selectedRequest.defaultStatus === 'approved' ? 'Approva Richiesta' :
                 selectedRequest.defaultStatus === 'rejected' ? 'Rifiuta Richiesta' : 'Dettagli Richiesta'}
              </h4>
              <button 
                onClick={() => setShowNotesForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Body Modal */}
            <div style={{ padding: '20px' }}>
              <div style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
<p style={{ margin: '0 0 10px 0' }}>
  <strong>Utente:</strong> {selectedRequest.userName || `${selectedRequest.userNome} ${selectedRequest.userCognome}`}
</p>
<p style={{ margin: '0 0 10px 0' }}>
  <strong>Tipo:</strong> {getRequestTypeName(selectedRequest.type)}
</p>

{/* ✅ AGGIUNTO: Tipologia permesso */}
{selectedRequest.type === 'permission' && selectedRequest.permissionType && (
  <p style={{ margin: '0 0 10px 0' }}>
    <strong>Tipologia permesso:</strong>{' '}
    {selectedRequest.permissionType === 'daily' && 'Giornaliero'}
    {selectedRequest.permissionType === 'hourly' && 'Orario'}
    {selectedRequest.permissionType === 'multi-day' && 'Multi-giorno'}
  </p>
)}

{/* ✅ AGGIUNTO: Orario per permessi orari */}
{selectedRequest.type === 'permission' && selectedRequest.permissionType === 'hourly' && (
  <p style={{ margin: '0 0 10px 0' }}>
    <strong>Orario:</strong>{' '}
    {formatTime24h(selectedRequest.timeFrom)} - {formatTime24h(selectedRequest.timeTo)}
    {' '}
    <small style={{ color: '#6c757d' }}>
      ({(() => {
        const [h1, m1] = selectedRequest.timeFrom.split(':').map(Number);
        const [h2, m2] = selectedRequest.timeTo.split(':').map(Number);
        const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
        const hours = Math.floor(diff / 60);
        const mins = diff % 60;
        return hours > 0 ? `${hours}h${mins > 0 ? ` ${mins}min` : ''}` : `${mins}min`;
      })()})
    </small>
  </p>
)}
<p style={{ margin: '0 0 10px 0' }}>
  <strong>Periodo:</strong> {formatDate(selectedRequest.dateFrom)}
  {selectedRequest.dateTo && selectedRequest.dateTo !== selectedRequest.dateFrom &&
    ` → ${formatDate(selectedRequest.dateTo)}`}
</p>

{/* ✅ AGGIUNTO: Giorni lavorativi per permessi multi-giorno */}
{selectedRequest.type === 'permission' && selectedRequest.permissionType === 'multi-day' && selectedRequest.workingDaysCount && (
  <p style={{ margin: '0 0 10px 0' }}>
    <strong>Giorni lavorativi:</strong> {selectedRequest.workingDaysCount}
  </p>
)}

{selectedRequest.notes && (
  <p style={{ margin: '0' }}>
    <strong>Note utente:</strong> {selectedRequest.notes}
  </p>
)}
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#495057'
                }}>
                  Note amministratore:
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Aggiungi note opzionali..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            
            {/* Footer Modal */}
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              padding: '20px',
              borderTop: '1px solid #dee2e6'
            }}>
              {selectedRequest.defaultStatus === 'approved' && (
                <button
                  onClick={() => handleRequestAction(true)}
                  disabled={processingRequest}
                  style={{
                    padding: '8px 16px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: processingRequest ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: processingRequest ? 0.6 : 1
                  }}
                >
                  {processingRequest ? 'Approvazione...' : 'Approva'}
                </button>
              )}
              {selectedRequest.defaultStatus === 'rejected' && (
                <button
                  onClick={() => handleRequestAction(false)}
                  disabled={processingRequest}
                  style={{
                    padding: '8px 16px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: processingRequest ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: processingRequest ? 0.6 : 1
                  }}
                >
                  {processingRequest ? 'Rifiuto...' : 'Rifiuta'}
                </button>
              )}
              <button
                onClick={() => setShowNotesForm(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal di Conferma Eliminazione */}
      {showDeleteConfirm && requestToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            {/* Header Modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              background: '#f8d7da'
            }}>
              <h4 style={{ margin: 0, color: '#721c24' }}>
                ⚠️ Conferma Eliminazione
              </h4>
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#721c24'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Body Modal */}
            <div style={{ padding: '20px' }}>
              <div style={{
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#721c24' }}>
                  Sei sicuro di voler eliminare questa richiesta approvata?
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#721c24' }}>
                  Il sistema eseguirà automaticamente:
                </p>
                <ul style={{ margin: '10px 0 0 30px', fontSize: '14px', color: '#721c24' }}>
                  <li>Eliminazione della richiesta dal database</li>
                  <li>Rimozione automatica delle lettere speciali dal calendario ore</li>
                  <li>Ripristino delle date interessate allo stato vuoto</li>
                </ul>
              </div>
              
              <div style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px'
              }}>
                <h5 style={{ margin: '0 0 10px 0' }}>Dettagli richiesta da eliminare:</h5>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Utente:</strong> {requestToDelete.userName || `${requestToDelete.userNome} ${requestToDelete.userCognome}`}
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Tipo:</strong> {getRequestTypeName(requestToDelete.type)}
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Periodo:</strong> {formatDate(requestToDelete.dateFrom)}
                  {requestToDelete.dateTo && requestToDelete.dateTo !== requestToDelete.dateFrom && 
                    ` → ${formatDate(requestToDelete.dateTo)}`}
                </p>
                {requestToDelete.adminNotes && (
                  <p style={{ margin: '0' }}>
                    <strong>Note admin:</strong> {requestToDelete.adminNotes}
                  </p>
                )}
              </div>
              
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                padding: '12px',
                marginTop: '15px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>🔄</span>
                  <strong>Desincronizzazione Automatica</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Le date precedentemente sincronizzate verranno automaticamente ripristinate 
                  allo stato vuoto nel calendario ore.
                </p>
              </div>
            </div>
            
            {/* Footer Modal */}
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              padding: '20px',
              borderTop: '1px solid #dee2e6'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                {isDeleting ? 'Eliminazione in corso...' : 'Elimina Definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeaveRequests;